import { useQuery } from "react-query";

import type {
  AgentStateDiff,
  AgentStateDocument,
  AgentStateSummary,
} from "../models/agentState";
import { queryKeys } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

// One call drives both the agent switcher and the revision dropdowns.
export const useAgentStateList = (projectId: string | undefined) => {
  const http = useAgentsHttp();
  return useQuery<AgentStateSummary[], Error>(
    queryKeys.agentStateList(projectId ?? null),
    async () => {
      if (!projectId) return [];
      const resp = await http<{ agents: AgentStateSummary[] }>(
        "/projects/agent-state/list",
        { project_id: projectId },
      );
      return resp.agents;
    },
    { enabled: Boolean(projectId), refetchOnWindowFocus: false },
  );
};

// ``version`` null means the current document.
export const useAgentStateDocument = (
  projectId: string | undefined,
  agent: string | null,
  version: number | null,
) => {
  const http = useAgentsHttp();
  return useQuery<AgentStateDocument | null, Error>(
    queryKeys.agentStateDocument(projectId ?? null, agent, version),
    async () => {
      if (!projectId || !agent) return null;
      return http<AgentStateDocument>("/projects/agent-state/get", {
        project_id: projectId,
        agent,
        ...(version == null ? {} : { version }),
      });
    },
    {
      enabled: Boolean(projectId && agent),
      refetchOnWindowFocus: false,
      // Stored revisions are append-only; only the current-document read moves.
      staleTime: version == null ? 30_000 : Infinity,
    },
  );
};

// ``fromVersion`` null diffs against an empty document (version 1). Server-side, so
// the browser carries no diff dependency.
export const useAgentStateDiff = (
  projectId: string | undefined,
  agent: string | null,
  fromVersion: number | null,
  toVersion: number | null,
  enabled: boolean,
) => {
  const http = useAgentsHttp();
  return useQuery<AgentStateDiff | null, Error>(
    queryKeys.agentStateDiff(projectId ?? null, agent, fromVersion, toVersion),
    async () => {
      if (!projectId || !agent || toVersion == null) return null;
      return http<AgentStateDiff>("/projects/agent-state/diff", {
        project_id: projectId,
        agent,
        from_version: fromVersion,
        to_version: toVersion,
      });
    },
    {
      enabled: enabled && Boolean(projectId && agent) && toVersion != null,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );
};
